import {
  Breadcrumbs,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Link,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import MailOutlineIcon from "@material-ui/icons/MailOutline";
import SaveIcon from "@material-ui/icons/Save";
import VpnKeyIcon from "@material-ui/icons/VpnKey";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import Title from "../../../Components/Title";
import { showPopupSnackbar } from "../../../redux/actions/snackbarActions";
import {
  checkRequiredPermissions,
  ECABIN,
  formatTimeWithTZOffset,
  getToken,
  requestErrorHandler,
  USER_GROUPS,
} from "../../../Utils/Common";
import EnvSettings from "../../../Utils/EnvSettings";
import { useStyles } from "../../../Utils/Styles";

const server = EnvSettings.server;

const EMPTY_RULE_SET = {
  name: "",
  channel: "email",
  field: "subject",
  operator: "contains",
  value: "",
  action: "skip_ticket_creation",
  active: true,
  priority: 100,
};

const RULE_SET_CHANNELS = ["email", "whatsapp", "all"];
const RULE_SET_FIELDS = [
  "subject",
  "sender",
  "senderEmail",
  "senderDomain",
  "recipient",
  "body",
  "phoneNumber",
  "messageType",
];
const RULE_SET_OPERATORS = [
  "contains",
  "equals",
  "startsWith",
  "endsWith",
  "regex",
];

const normalizeEmailAddress = (value) => {
  return String(value || "")
    .trim()
    .toLowerCase();
};

const callGmailSettingsEndpoint = async (endpoint, callBody) => {
  const response = await fetch(server + endpoint, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      token: getToken(),
      ...(callBody || {}),
    }),
  });
  const result = await response.json().catch(() => ({
    error: true,
    message: "Gmail endpoint returned an invalid response",
  }));

  if (!response.ok && !result.error) {
    return {
      ...result,
      error: true,
      message: response.statusText || "Gmail request failed",
    };
  }
  return result;
};

const getOAuthReturnUrl = () => {
  var queryParams = new URLSearchParams(window.location.search);
  var cleanedSearch;

  queryParams.delete("gmailOAuth");
  queryParams.delete("gmailOAuthMessage");
  cleanedSearch = queryParams.toString();

  return (
    window.location.origin +
    window.location.pathname +
    (cleanedSearch ? "?" + cleanedSearch : "") +
    window.location.hash
  );
};

const cleanOAuthQueryParams = () => {
  var queryParams = new URLSearchParams(window.location.search);
  var cleanedSearch;

  queryParams.delete("gmailOAuth");
  queryParams.delete("gmailOAuthMessage");
  cleanedSearch = queryParams.toString();
  window.history.replaceState(
    {},
    document.title,
    window.location.pathname +
      (cleanedSearch ? "?" + cleanedSearch : "") +
      window.location.hash,
  );
};

const formatDate = (value) => {
  if (!value) {
    return "";
  }
  if (!Number.isNaN(Number(value))) {
    return formatTimeWithTZOffset(
      new Date(Number(value)).toISOString(),
      ECABIN,
    );
  }
  return formatTimeWithTZOffset(String(value), ECABIN);
};

const StatusRow = ({ label, value }) => (
  <Grid container spacing={1}>
    <Grid item xs={12} sm={4}>
      <Typography color="textSecondary" variant="body2">
        {label}
      </Typography>
    </Grid>
    <Grid item xs={12} sm={8}>
      <Typography variant="body2">{value || "-"}</Typography>
    </Grid>
  </Grid>
);

export default function TicketSettings() {
  const { t } = useTranslation();
  const classes = useStyles();
  const dispatch = useDispatch();
  const [emailAddress, setEmailAddress] = useState("");
  const [mailbox, setMailbox] = useState();
  const [mailboxes, setMailboxes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [oauthLoading, setOauthLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  const [ticketConfig, setTicketConfig] = useState({
    stalingPeriod: "",
    automaticStalingActive: false,
  });
  const [ticketConfigLoading, setTicketConfigLoading] = useState(false);
  const [ticketConfigSaving, setTicketConfigSaving] = useState(false);
  const [ruleSets, setRuleSets] = useState([]);
  const [ruleSetForm, setRuleSetForm] = useState(EMPTY_RULE_SET);
  const [ruleSetsLoading, setRuleSetsLoading] = useState(false);
  const [ruleSetSaving, setRuleSetSaving] = useState(false);

  const loadSettings = useCallback(async () => {
    setLoading(true);
    try {
      var result = await callGmailSettingsEndpoint("/get_gmail_settings");
      requestErrorHandler(result, dispatch);
      if (!result.error) {
        setMailbox(result.mailbox);
        setMailboxes(result.mailboxes || []);
        setEmailAddress(result.mailbox?.emailAddress || "");
      }
    } catch (error) {
      requestErrorHandler(
        {
          error: true,
          message: error.message || t("maintenance.gmailSettingsLoadFailed"),
        },
        dispatch,
      );
    } finally {
      setLoading(false);
    }
  }, [dispatch, t]);

  const loadTicketConfig = useCallback(async () => {
    setTicketConfigLoading(true);
    try {
      var result = await callGmailSettingsEndpoint("/get_ticket_config");
      requestErrorHandler(result, dispatch);
      if (!result.error) {
        setTicketConfig({
          stalingPeriod: result.ticketConfig?.stalingPeriod || "",
          automaticStalingActive: Boolean(
            Number(result.ticketConfig?.automaticStalingActive),
          ),
        });
      }
    } catch (error) {
      requestErrorHandler(
        {
          error: true,
          message: error.message || t("maintenance.ticketConfigLoadFailed"),
        },
        dispatch,
      );
    } finally {
      setTicketConfigLoading(false);
    }
  }, [dispatch, t]);

  const loadTicketCreationRuleSets = useCallback(async () => {
    setRuleSetsLoading(true);
    try {
      var result = await callGmailSettingsEndpoint(
        "/get_ticket_creation_rule_sets",
      );
      requestErrorHandler(result, dispatch);
      if (!result.error) {
        setRuleSets(result.ruleSets || []);
      }
    } catch (error) {
      requestErrorHandler(
        {
          error: true,
          message:
            error.message || t("maintenance.ticketRuleSetsLoadFailed"),
        },
        dispatch,
      );
    } finally {
      setRuleSetsLoading(false);
    }
  }, [dispatch, t]);

  const saveSettings = async () => {
    var normalizedEmailAddress = normalizeEmailAddress(emailAddress);

    if (!normalizedEmailAddress) {
      dispatch(
        showPopupSnackbar({
          error: true,
          message: t("maintenance.gmailMailboxRequired"),
        }),
      );
      return;
    }

    setSaving(true);
    try {
      var result = await callGmailSettingsEndpoint("/set_gmail_settings", {
        emailAddress: normalizedEmailAddress,
      });
      requestErrorHandler(result, dispatch);
      if (!result.error) {
        setMailbox(result.mailbox);
        setMailboxes(result.mailboxes || []);
        setEmailAddress(result.mailbox?.emailAddress || normalizedEmailAddress);
        dispatch(
          showPopupSnackbar({
            error: false,
            message: t("maintenance.gmailMailboxSaved"),
          }),
        );
        return result.mailbox;
      }
    } catch (error) {
      requestErrorHandler(
        {
          error: true,
          message: error.message || t("maintenance.gmailSettingsSaveFailed"),
        },
        dispatch,
      );
    } finally {
      setSaving(false);
    }
  };

  const startOAuth = async () => {
    var normalizedEmailAddress = normalizeEmailAddress(emailAddress);

    if (!normalizedEmailAddress) {
      dispatch(
        showPopupSnackbar({
          error: true,
          message: t("maintenance.gmailMailboxRequired"),
        }),
      );
      return;
    }

    setOauthLoading(true);
    try {
      var savedMailbox =
        mailbox?.emailAddress === normalizedEmailAddress && mailbox.current
          ? mailbox
          : await saveSettings();

      if (!savedMailbox) {
        return;
      }

      var result = await callGmailSettingsEndpoint("/get_gmail_oauth_url", {
        mailboxEmailAddress: normalizedEmailAddress,
        returnUrl: getOAuthReturnUrl(),
      });
      requestErrorHandler(result, dispatch);
      if (result.error || !result.oauthUrl) {
        return;
      }
      window.location.assign(result.oauthUrl);
    } catch (error) {
      requestErrorHandler(
        {
          error: true,
          message: error.message || t("maintenance.gmailOAuthFailed"),
        },
        dispatch,
      );
    } finally {
      setOauthLoading(false);
    }
  };

  const saveTicketConfig = async () => {
    var stalingPeriod = Number.parseInt(ticketConfig.stalingPeriod, 10);

    if (!Number.isInteger(stalingPeriod) || stalingPeriod < 1) {
      dispatch(
        showPopupSnackbar({
          error: true,
          message: t("maintenance.stalingPeriodRequired"),
        }),
      );
      return;
    }

    setTicketConfigSaving(true);
    try {
      var result = await callGmailSettingsEndpoint("/set_ticket_config", {
        ticketConfig: {
          stalingPeriod: stalingPeriod,
          automaticStalingActive: ticketConfig.automaticStalingActive ? 1 : 0,
        },
      });
      requestErrorHandler(result, dispatch);
      if (!result.error) {
        setTicketConfig({
          stalingPeriod: result.ticketConfig?.stalingPeriod || stalingPeriod,
          automaticStalingActive: Boolean(
            Number(result.ticketConfig?.automaticStalingActive),
          ),
        });
        dispatch(
          showPopupSnackbar({
            error: false,
            message: t("maintenance.ticketConfigSaved"),
          }),
        );
      }
    } catch (error) {
      requestErrorHandler(
        {
          error: true,
          message: error.message || t("maintenance.ticketConfigSaveFailed"),
        },
        dispatch,
      );
    } finally {
      setTicketConfigSaving(false);
    }
  };

  const resetRuleSetForm = () => {
    setRuleSetForm({ ...EMPTY_RULE_SET });
  };

  const saveTicketCreationRuleSet = async () => {
    if (!ruleSetForm.name || !ruleSetForm.value) {
      dispatch(
        showPopupSnackbar({
          error: true,
          message: t("maintenance.ticketRuleSetRequired"),
        }),
      );
      return;
    }

    setRuleSetSaving(true);
    try {
      var result = await callGmailSettingsEndpoint(
        "/save_ticket_creation_rule_set",
        {
          ruleSet: {
            ...ruleSetForm,
            active: ruleSetForm.active ? 1 : 0,
            priority: Number.parseInt(ruleSetForm.priority, 10) || 100,
          },
        },
      );
      requestErrorHandler(result, dispatch);
      if (!result.error) {
        await loadTicketCreationRuleSets();
        resetRuleSetForm();
        dispatch(
          showPopupSnackbar({
            error: false,
            message: t("maintenance.ticketRuleSetSaved"),
          }),
        );
      }
    } catch (error) {
      requestErrorHandler(
        {
          error: true,
          message: error.message || t("maintenance.ticketRuleSetSaveFailed"),
        },
        dispatch,
      );
    } finally {
      setRuleSetSaving(false);
    }
  };

  const editTicketCreationRuleSet = (ruleSet) => {
    setRuleSetForm({
      ...EMPTY_RULE_SET,
      ...ruleSet,
      active: Boolean(Number(ruleSet.active)),
    });
  };

  const deleteTicketCreationRuleSet = async (ruleSet) => {
    setRuleSetSaving(true);
    try {
      var result = await callGmailSettingsEndpoint(
        "/delete_ticket_creation_rule_set",
        {
          id: ruleSet.id,
        },
      );
      requestErrorHandler(result, dispatch);
      if (!result.error) {
        await loadTicketCreationRuleSets();
        if (ruleSetForm.id === ruleSet.id) {
          resetRuleSetForm();
        }
        dispatch(
          showPopupSnackbar({
            error: false,
            message: t("maintenance.ticketRuleSetDeleted"),
          }),
        );
      }
    } catch (error) {
      requestErrorHandler(
        {
          error: true,
          message: error.message || t("maintenance.ticketRuleSetDeleteFailed"),
        },
        dispatch,
      );
    } finally {
      setRuleSetSaving(false);
    }
  };

  useEffect(() => {
    var queryParams = new URLSearchParams(window.location.search);
    var gmailOAuthStatus = queryParams.get("gmailOAuth");
    var gmailOAuthMessage = queryParams.get("gmailOAuthMessage");

    if (gmailOAuthStatus === "success") {
      setStatusMessage(t("maintenance.gmailOAuthConnectedMessage"));
      dispatch(
        showPopupSnackbar({
          error: false,
          message: t("maintenance.gmailOAuthConnectedMessage"),
        }),
      );
      cleanOAuthQueryParams();
    } else if (gmailOAuthStatus) {
      setStatusMessage(gmailOAuthMessage || t("maintenance.gmailOAuthFailed"));
      dispatch(
        showPopupSnackbar({
          error: true,
          message: gmailOAuthMessage || t("maintenance.gmailOAuthFailed"),
        }),
      );
      cleanOAuthQueryParams();
    }

    loadSettings();
    loadTicketConfig();
    loadTicketCreationRuleSets();
  }, [
    dispatch,
    loadSettings,
    loadTicketConfig,
    loadTicketCreationRuleSets,
    t,
  ]);

  if (!checkRequiredPermissions([USER_GROUPS.SUPER_USER])) {
    return <Typography>{t("unauthorised")}</Typography>;
  }

  return (
    <Grid container justify="center" spacing={1}>
      <Grid item xs={12}>
        <Breadcrumbs aria-label="breadcrumb">
          <Link color="inherit" href="/cpanel/maintenance">
            {t("maintenance.header")}
          </Link>
          <Typography>{t("maintenance.settings")}</Typography>
        </Breadcrumbs>
      </Grid>

      <Grid item xs={12}>
        <Paper className={classes.paper}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12}>
              <Title>{t("maintenance.gmailSettings")}</Title>
            </Grid>

            <Grid item xs={12} md={8}>
              <TextField
                disabled={loading || saving || oauthLoading}
                fullWidth
                label={t("maintenance.gmailMailboxEmailAddress")}
                onChange={(event) => setEmailAddress(event.target.value)}
                value={emailAddress}
                variant="outlined"
              />
            </Grid>
            <Grid
              item
              xs={12}
              md={4}
              style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}
            >
              <Button
                color="primary"
                disabled={loading || saving || oauthLoading}
                onClick={saveSettings}
                variant="contained"
              >
                {saving ? (
                  <CircularProgress
                    color="inherit"
                    size={20}
                    style={{ marginRight: 8 }}
                  />
                ) : (
                  <SaveIcon style={{ fontSize: 20, marginRight: 8 }} />
                )}
                {t("maintenance.saveGmailMailbox")}
              </Button>
              <Button
                color="primary"
                disabled={loading || saving || oauthLoading}
                onClick={startOAuth}
                variant="contained"
              >
                {oauthLoading ? (
                  <CircularProgress
                    color="inherit"
                    size={20}
                    style={{ marginRight: 8 }}
                  />
                ) : (
                  <VpnKeyIcon style={{ fontSize: 20, marginRight: 8 }} />
                )}
                {t("maintenance.startGmailOAuth")}
              </Button>
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            {loading ? (
              <Grid item xs={12}>
                <CircularProgress />
              </Grid>
            ) : (
              <React.Fragment>
                <Grid item xs={12} md={6}>
                  <StatusRow
                    label={t("maintenance.configuredMailbox")}
                    value={
                      mailbox?.emailAddress || t("maintenance.noGmailMailbox")
                    }
                  />
                  <StatusRow
                    label={t("maintenance.oauthStatus")}
                    value={
                      mailbox?.oauthConfigured
                        ? t("maintenance.oauthConnected")
                        : t("maintenance.oauthNotConnected")
                    }
                  />
                  <StatusRow
                    label={t("maintenance.oauthUpdatedAt")}
                    value={formatDate(mailbox?.oauthUpdatedAt)}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <StatusRow
                    label={t("maintenance.watchExpiration")}
                    value={
                      formatDate(
                        mailbox?.watchExpirationDate ||
                          mailbox?.watchExpiration,
                      ) || t("maintenance.watchNotActive")
                    }
                  />
                  <StatusRow
                    label={t("maintenance.historyId")}
                    value={mailbox?.historyId}
                  />
                  <StatusRow
                    label={t("maintenance.status")}
                    value={statusMessage}
                  />
                </Grid>
              </React.Fragment>
            )}
          </Grid>
          {mailboxes.length ? (
            <>
              <Grid item xs={12} style={{ marginTop: 10 }}>
                <Title>{t("maintenance.gmailMailboxes")}</Title>
              </Grid>
              {mailboxes.map((item) => (
                <Grid item xs={12} md={6} key={item.id || item.emailAddress}>
                  <Grid container alignItems="center" spacing={1}>
                    <Grid item>
                      <MailOutlineIcon
                        color={item.current ? "primary" : "inherit"}
                      />
                    </Grid>
                    <Grid item xs>
                      <Typography>{item.emailAddress}</Typography>
                      <Typography color="textSecondary" variant="body2">
                        {item.current ? t("maintenance.currentMailbox") : ""}
                        {item.oauthConfigured
                          ? " " + t("maintenance.oauthConnected")
                          : " " + t("maintenance.oauthNotConnected")}
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
              ))}
            </>
          ) : null}
        </Paper>
      </Grid>

      <Grid item xs={12}>
        <Paper className={classes.paper}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12}>
              <Title>{t("maintenance.ticketSettings")}</Title>
            </Grid>
            {ticketConfigLoading ? (
              <Grid item xs={12}>
                <CircularProgress />
              </Grid>
            ) : (
              <React.Fragment>
                <Grid item xs={12} md={4}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={ticketConfig.automaticStalingActive}
                        color="primary"
                        disabled={ticketConfigSaving}
                        onChange={(event) =>
                          setTicketConfig({
                            ...ticketConfig,
                            automaticStalingActive: event.target.checked,
                          })
                        }
                      />
                    }
                    label={t("maintenance.automaticStalingActive")}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    disabled={ticketConfigSaving}
                    fullWidth
                    inputProps={{ min: 1 }}
                    label={t("maintenance.stalingPeriodDays")}
                    onChange={(event) =>
                      setTicketConfig({
                        ...ticketConfig,
                        stalingPeriod: event.target.value,
                      })
                    }
                    type="number"
                    value={ticketConfig.stalingPeriod}
                    variant="outlined"
                  />
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={4}
                  style={{ display: "flex", justifyContent: "flex-end" }}
                >
                  <Button
                    color="primary"
                    disabled={ticketConfigSaving}
                    onClick={saveTicketConfig}
                    variant="contained"
                  >
                    {ticketConfigSaving ? (
                      <CircularProgress
                        color="inherit"
                        size={20}
                        style={{ marginRight: 8 }}
                      />
                    ) : (
                      <SaveIcon style={{ fontSize: 20, marginRight: 8 }} />
                    )}
                    {t("maintenance.saveTicketConfig")}
                  </Button>
                </Grid>
              </React.Fragment>
            )}
          </Grid>
        </Paper>
      </Grid>

      <Grid item xs={12}>
        <Paper className={classes.paper}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12}>
              <Title>{t("maintenance.ticketCreationRuleSets")}</Title>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                disabled={ruleSetSaving}
                fullWidth
                label={t("maintenance.ruleSetName")}
                onChange={(event) =>
                  setRuleSetForm({
                    ...ruleSetForm,
                    name: event.target.value,
                  })
                }
                value={ruleSetForm.name}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                disabled={ruleSetSaving}
                fullWidth
                label={t("maintenance.ruleSetChannel")}
                onChange={(event) =>
                  setRuleSetForm({
                    ...ruleSetForm,
                    channel: event.target.value,
                  })
                }
                select
                value={ruleSetForm.channel}
                variant="outlined"
              >
                {RULE_SET_CHANNELS.map((channel) => (
                  <MenuItem key={channel} value={channel}>
                    {t("maintenance.ruleSetChannel_" + channel)}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                disabled={ruleSetSaving}
                fullWidth
                label={t("maintenance.ruleSetField")}
                onChange={(event) =>
                  setRuleSetForm({
                    ...ruleSetForm,
                    field: event.target.value,
                  })
                }
                select
                value={ruleSetForm.field}
                variant="outlined"
              >
                {RULE_SET_FIELDS.map((field) => (
                  <MenuItem key={field} value={field}>
                    {t("maintenance.ruleSetField_" + field)}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                disabled={ruleSetSaving}
                fullWidth
                label={t("maintenance.ruleSetOperator")}
                onChange={(event) =>
                  setRuleSetForm({
                    ...ruleSetForm,
                    operator: event.target.value,
                  })
                }
                select
                value={ruleSetForm.operator}
                variant="outlined"
              >
                {RULE_SET_OPERATORS.map((operator) => (
                  <MenuItem key={operator} value={operator}>
                    {t("maintenance.ruleSetOperator_" + operator)}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                disabled={ruleSetSaving}
                fullWidth
                inputProps={{ min: 1 }}
                label={t("maintenance.ruleSetPriority")}
                onChange={(event) =>
                  setRuleSetForm({
                    ...ruleSetForm,
                    priority: event.target.value,
                  })
                }
                type="number"
                value={ruleSetForm.priority}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} md={8}>
              <TextField
                disabled={ruleSetSaving}
                fullWidth
                label={t("maintenance.ruleSetValue")}
                onChange={(event) =>
                  setRuleSetForm({
                    ...ruleSetForm,
                    value: event.target.value,
                  })
                }
                value={ruleSetForm.value}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={ruleSetForm.active}
                    color="primary"
                    disabled={ruleSetSaving}
                    onChange={(event) =>
                      setRuleSetForm({
                        ...ruleSetForm,
                        active: event.target.checked,
                      })
                    }
                  />
                }
                label={t("maintenance.ruleSetActive")}
              />
            </Grid>
            <Grid
              item
              xs={12}
              md={2}
              style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}
            >
              <Button
                color="primary"
                disabled={ruleSetSaving}
                onClick={saveTicketCreationRuleSet}
                variant="contained"
              >
                {ruleSetSaving ? (
                  <CircularProgress
                    color="inherit"
                    size={20}
                    style={{ marginRight: 8 }}
                  />
                ) : ruleSetForm.id ? (
                  <SaveIcon style={{ fontSize: 20, marginRight: 8 }} />
                ) : (
                  <AddIcon style={{ fontSize: 20, marginRight: 8 }} />
                )}
                {ruleSetForm.id
                  ? t("maintenance.saveRuleSet")
                  : t("maintenance.addRuleSet")}
              </Button>
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>
            {ruleSetsLoading ? (
              <Grid item xs={12}>
                <CircularProgress />
              </Grid>
            ) : ruleSets.length ? (
              ruleSets.map((ruleSet) => (
                <Grid item xs={12} key={ruleSet.id}>
                  <Grid
                    container
                    alignItems="center"
                    spacing={1}
                    style={{ borderBottom: "1px solid #e0e0e0", paddingBottom: 8 }}
                  >
                    <Grid item xs={12} md={3}>
                      <Typography>{ruleSet.name}</Typography>
                      <Typography color="textSecondary" variant="body2">
                        {ruleSet.active
                          ? t("maintenance.ruleSetActive")
                          : t("maintenance.ruleSetInactive")}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={7}>
                      <Typography variant="body2">
                        {t("maintenance.ruleSetChannel_" + ruleSet.channel)} /
                        {" " + t("maintenance.ruleSetField_" + ruleSet.field)}{" "}
                        {t(
                          "maintenance.ruleSetOperator_" +
                            ruleSet.operator,
                        )}{" "}
                        {ruleSet.value}
                      </Typography>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      md={2}
                      style={{ display: "flex", justifyContent: "flex-end" }}
                    >
                      <IconButton
                        disabled={ruleSetSaving}
                        onClick={() => editTicketCreationRuleSet(ruleSet)}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        disabled={ruleSetSaving}
                        onClick={() => deleteTicketCreationRuleSet(ruleSet)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Grid>
                  </Grid>
                </Grid>
              ))
            ) : (
              <Grid item xs={12}>
                <Typography color="textSecondary">
                  {t("maintenance.noTicketCreationRuleSets")}
                </Typography>
              </Grid>
            )}
          </Grid>
        </Paper>
      </Grid>
    </Grid>
  );
}
